import React, { useState, useEffect, useCallback } from 'react';
import CenterSpinner from '../../../../../general/Loadings/CenterSpinner';
import { CRow, CCol, CBadge } from '@coreui/react';
import CLabel from '../../../../../migration/CLabel';

import { callTokenApi } from '../../../../../../apiCaller';
import { formatDateTimeZone, toastError } from '../../../../../../utils';
import { ADMIN_PERSON_IDENTIFIER_RETRIEVE, DEFAULT_DATE_TIME_FORMAT, LLM_STATUS } from '../../../../../../constants';

const renderBoolean = (value, trueText = "Yes", falseText = "No", undefinedText = "none") => {
    if (typeof value === 'boolean') {
        return value ? <CBadge color="success" shape="rounded-pill">{trueText}</CBadge> : <CBadge color="danger" shape="rounded-pill">{falseText}</CBadge>;
    }
    return <CBadge color="secondary" shape="rounded-pill">{undefinedText}</CBadge>;
};

export const renderJsonData = (jsonData, fieldName = "Data") => {
    if (jsonData === null || jsonData === undefined) return <small className="text-muted">none</small>;
    if (typeof jsonData === 'object') {
        if (Array.isArray(jsonData) && jsonData.length === 0) return <small className="text-muted">Empty</small>;
        return (
            <details className="json-details">
                <summary style={{ cursor: 'pointer', fontWeight: '500' }}>View {fieldName} (JSON)</summary>
                <pre style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    background: '#f8f9fa',
                    padding: '10px 10px 0px',
                    borderRadius: '4px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    marginBottom: '0px'
                }}>
                    {JSON.stringify(jsonData, null, 2)}
                </pre>
            </details>
        );
    }
    return String(jsonData);
};

const PersonIdentifier = ({ attributes }) => {
    const { id: personId, timeZone } = attributes;

    const [identifierData, setIdentifierData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        if (!personId) {
            setError("Person ID is missing.");
            setIsLoading(false);
            setIdentifierData(null);
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const response = await callTokenApi(`${ADMIN_PERSON_IDENTIFIER_RETRIEVE.replace(':id', personId)}`, 'GET', null);

            if (response.status === 200 && response.data?.success) {
                setIdentifierData(response.data.data);
            } else {
                const errorMsg = response.data?.message || `Failed to fetch person data (status: ${response.status})`;
                setError(errorMsg);
                toastError({ data: { message: errorMsg } });
                setIdentifierData(null);
            }
        } catch (err) {
            console.error("API call failed for person detail:", err);
            const errorMessage = err.message || 'An unexpected error occurred.';
            setError(errorMessage);
            toastError({ data: { message: errorMessage } });
            setIdentifierData(null);
        } finally {
            setIsLoading(false);
        }
    }, [personId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    if (isLoading) {
        return <CenterSpinner />;
    }

    if (error) {
        return <div className="alert alert-danger" role="alert">Error: {error}</div>;
    }

    if (!identifierData) {
        return <div className="alert alert-info">No person data found for ID: {personId}</div>;
    }

    const { llmStatus } = identifierData;
    const personTypeHidden = ['linkedin'];

    const fieldsToDisplay = [
        { label: "Person ID", value: identifierData.personId || "none" },
        { label: "Company ID (Linked)", value: identifierData.companyId || "none" },
        { label: "Visitor ID", value: identifierData.visitorId || "none" },
        { label: "Session ID", value: identifierData.sessionId || "none" },
        { label: "Related ID", value: identifierData.relatedId || "none" },
        { label: "Type", value: identifierData.type },
        { label: "Value", value: identifierData.value, type: 'multiline' },
        { label: "Source", value: identifierData.source },
        { label: "Source Path", value: identifierData.sourcePath },
        { label: "Action", value: identifierData.action },

        // Validation Section
        { label: "Valid Status", value: identifierData.validStatus, type: 'statusBadge' },
        { label: "Validation Run", value: renderBoolean(identifierData.validRun, "Run", "Not Run") },
        { label: "Validation Score", value: identifierData.validScore },
        { label: "Validated At", value: formatDateTimeZone(identifierData.validAt, DEFAULT_DATE_TIME_FORMAT, timeZone) },
        { label: "Validation Data", value: renderJsonData(identifierData.validData, "Validation Data") },

        // Company Matching
        { label: "Valid Company Match", value: !identifierData.companyId ? 'none' : renderBoolean(identifierData.validCompanyMatch) },

        // Person Details
        { label: "Identifier Type", value: identifierData.personType },
        { label: "Overall Score", value: identifierData.score },

        // Enrichment Section
        { label: "Enrich Status", value: renderBoolean(identifierData.enrichStatus), hidden: personTypeHidden.includes(identifierData.type) },
        { label: "Enrichment Score", value: identifierData.enrichScore, hidden: personTypeHidden.includes(identifierData.type) },
        { label: "Enrichment Type", value: identifierData.enrichType, hidden: personTypeHidden.includes(identifierData.type) },

        // B2B Enrichment
        { label: "B2B Enrichment", value: renderBoolean(identifierData.enrichB2B), hidden: personTypeHidden.includes(identifierData.type) },
        { label: "B2B Run", value: renderBoolean(identifierData.enrichB2BRun), hidden: personTypeHidden.includes(identifierData.type) },
        { label: "B2B Enriched At", value: formatDateTimeZone(identifierData.enrichB2BAt, DEFAULT_DATE_TIME_FORMAT, timeZone), hidden: personTypeHidden.includes(identifierData.type) },
        { label: "B2B Data", value: renderJsonData(identifierData.enrichB2BData, "B2B Enrichment Data"), hidden: personTypeHidden.includes(identifierData.type) },

        // B2C Enrichment
        { label: "B2C Enrichment", value: renderBoolean(identifierData.enrichB2C), hidden: personTypeHidden.includes(identifierData.type) },
        { label: "B2C Run", value: renderBoolean(identifierData.enrichB2CRun), hidden: personTypeHidden.includes(identifierData.type) },
        { label: "B2C Enriched At", value: formatDateTimeZone(identifierData.enrichB2CAt, DEFAULT_DATE_TIME_FORMAT, timeZone), hidden: personTypeHidden.includes(identifierData.type) },
        { label: "B2C Data", value: renderJsonData(identifierData.enrichB2CRun ? identifierData.enrichB2CData : null, "B2C Enrichment Data"), hidden: personTypeHidden.includes(identifierData.type) },

        // LLM Section
        { label: "LLM Status", value: renderBoolean(llmStatus === LLM_STATUS.GOOD, llmStatus, llmStatus), type: 'statusBadge' },
        { label: "LLM Run", value: renderBoolean(identifierData.llmRun, "Run", "Not Run") },
        { label: "LLM Score", value: identifierData.llmScore },
        { label: "LLM Processed At", value: formatDateTimeZone(identifierData.llmAt, DEFAULT_DATE_TIME_FORMAT, timeZone) },
        { label: "LLM Data", value: renderJsonData(identifierData.llmData, "LLM Data") },

        // Testing Flag
        { label: "Is Testing", value: renderBoolean(identifierData.isTesting) },

        { label: "Record Created At", value: formatDateTimeZone(identifierData.created_at, DEFAULT_DATE_TIME_FORMAT, timeZone) },
        { label: "Record Updated At", value: formatDateTimeZone(identifierData.updated_at, DEFAULT_DATE_TIME_FORMAT, timeZone) },
    ];

    const renderFieldValue = (field) => {
        if (field.type === 'email' && field.value) {
            return <a href={`mailto:${field.value}`}>{field.value}</a>;
        }
        if (field.type === 'url' && field.value) {
            return <a href={field.value} target="_blank" rel="noopener noreferrer">{field.value}</a>;
        }
        return field.value ?? <small className="text-muted">none</small>;
    };

    return (
        <div className="rule-detail">
            <h3 className='mb-4'>
                Person Identifier Details: {identifierData.firstName || ''} {identifierData.lastName || ''}
            </h3>

            <CRow>
                {fieldsToDisplay.map(field => (
                    <React.Fragment key={field.label}>
                        <CCol md="3" className="mb-2">
                            <CLabel className="fw-semibold">{field.label}:</CLabel>
                        </CCol>

                        <CCol md="9" className="mb-2">
                            {renderFieldValue(field)}
                        </CCol>
                    </React.Fragment>
                ))}
            </CRow>
        </div>
    );
};

export default PersonIdentifier;